import { PlaceRef } from "@/types/entity"
import { Airline } from "@/types/flights/airline"
import {
  FlightBookingMode,
  PCIVaultCaptureEndpoint,
  PCIVaultCaptureResponse,
} from "@/types/flights/flight-booking"
import { FlightBookingApi } from "@/types/flights/flight-booking-api"
import { FlightContractUpgrade } from "@/types/flights/flight-contract-upgrade"
import { FlightItineraryApi } from "@/types/flights/flight-itinerary-api"
import {
  FlightOption,
  FlightOptionPayload,
} from "@/types/flights/flight-option"
import { FlightTravelers } from "@/types/flights/flight-search-options"
import { FlightSearchParams } from "@/types/flights/flight-search-params"
import { FlightSearchResult } from "@/types/flights/flight-search-result"
import {
  FlightSeatMapDetails,
  FlightSeatMapResponse,
} from "@/types/flights/flight-seat-map"
import {
  FlightSimulationFireBody,
  FlightSimulationResponse,
  FlightTriageResponse,
} from "@/types/flights/live-status"
import {
  AirportTransitPoint,
  FlightTransitPoint,
} from "@/types/flights/transit-points"
import {
  CheckoutError,
  VendorCheckoutResponse,
} from "@/types/flights/vendor-checkout"
import { Itinerary, ItineraryEvent } from "@/types/itinerary"
import { Trip } from "@/types/trip"
import { Optional } from "@/types/util"
import rawAxios from "axios"

import { BaseApi, BaseApiRequestConfig } from "@/lib/base-api"
import { dayToISO } from "./date-utils"
import {
  FlightBookingFormOptions,
  FlightTravelerFormValidated,
} from "./flight-form-utils"
import type { CreditCardFormValidated } from "./form-utils"
import type { BillingFormValidated } from "./phone-utils"

export interface SaveFlightItinerary {
  contract_id?: string
  airlines: Airline[]
  favorite?: boolean
  api_log_ids?: string[]
  // For Sabre: send full flight data instead of contract_id
  flight_data?: FlightOption | FlightOptionPayload
  selected_upgrade?: FlightContractUpgrade
}

export interface FlightConfig {
  provider: "sabre" | "priceline"
}

export class FlightApi extends BaseApi {
  getConfig(): Promise<FlightConfig> {
    return this.get<FlightConfig>("/api/flights/provider")
  }

  // Mindtrip-internal disruption simulator (backs the flight card's debug-mode
  // Simulate dialog). Fires synthetic webhook events through the real pipeline.
  simulationMakeTrackable(
    eventId: number
  ): Promise<{ events: ItineraryEvent[] }> {
    return this.post<{ events: ItineraryEvent[] }>(
      `/api/flights/simulation/${eventId}/make_trackable`,
      {}
    )
  }

  simulationFire(
    eventId: number,
    body: FlightSimulationFireBody
  ): Promise<FlightSimulationResponse> {
    return this.post<FlightSimulationResponse, FlightSimulationFireBody>(
      `/api/flights/simulation/${eventId}/fire`,
      body
    )
  }

  simulationReset(eventId: number): Promise<FlightSimulationResponse> {
    return this.post<FlightSimulationResponse>(
      `/api/flights/simulation/${eventId}/reset`,
      {}
    )
  }

  simulationTriage(eventId: number): Promise<FlightTriageResponse> {
    return this.post<FlightTriageResponse>(
      `/api/flights/simulation/${eventId}/triage`,
      {}
    )
  }

  departureSearch(
    params: FlightSearchParams,
    customConfig?: BaseApiRequestConfig
  ): Promise<FlightSearchResult> {
    const body = {
      slice_details: params.slice_details.map((slice) => ({
        origin_iata: slice.origin_iata,
        origin_ref: slice.origin_ref,
        destination_iata: slice.destination_iata,
        destination_ref: slice.destination_ref,
        departure_date: dayToISO(slice.departure_date),
      })),
      cabin_class: params.cabin_class,
      currency: params.currency,
      adults: params.adults,
      children: params.children,
      infants_on_lap: params.infants_on_lap,
      infants_in_seat: params.infants_in_seat,
      max_connections: params.max_connections,
    }

    return this.post<FlightSearchResult>(
      `/api/flights/departure_search`,
      body,
      customConfig
    )
  }

  returnSearch(
    returnId: string,
    departureApiLogId: Optional<string>,
    customConfig?: BaseApiRequestConfig
  ): Promise<FlightSearchResult> {
    const query = new URLSearchParams()

    if (departureApiLogId) {
      query.set("departure_api_log_id", departureApiLogId)
    }

    return this.get<FlightSearchResult>(
      `/api/flights/return_search/${returnId}?${query.toString()}`,
      customConfig
    )
  }

  getContract(
    contractId: string | undefined,
    customConfig?: BaseApiRequestConfig,
    flightData?: FlightOption | FlightOptionPayload,
    selectedUpgrade?: FlightContractUpgrade,
    travelers?: FlightTravelers
  ): Promise<FlightItineraryApi> {
    if (flightData) {
      return this.post<FlightItineraryApi>(
        `/api/flights/contract`,
        {
          flight_data: flightData,
          selected_upgrade: selectedUpgrade,
          passengers: travelers
            ? {
                adults: travelers.adult_count,
                children: travelers.child_count,
                infants_in_seat: travelers.infant_in_seat_count,
                infants_on_lap: travelers.infant_on_lap_count,
              }
            : undefined,
        },
        customConfig
      )
    }
    return this.get<FlightItineraryApi>(
      `/api/flights/contract/${contractId}`,
      customConfig
    )
  }

  locationSearch(
    query: string,
    only?: Optional<"airport" | "geo">,
    customConfig?: BaseApiRequestConfig
  ) {
    const params = new URLSearchParams()
    params.set("query", query)
    if (only) {
      params.set("only", only)
    }
    return this.get<{ transit_points: FlightTransitPoint[] }>(
      `/api/flights/locations/search?${params.toString()}`,
      customConfig
    )
  }

  getLocation(
    placeRef: PlaceRef,
    customConfig?: BaseApiRequestConfig
  ): Promise<FlightTransitPoint> {
    return this.get<FlightTransitPoint>(
      `/api/flights/locations/${placeRef}`,
      customConfig
    )
  }

  getLocationByIata(
    iata: string,
    customConfig?: BaseApiRequestConfig
  ): Promise<AirportTransitPoint> {
    return this.get<AirportTransitPoint>(
      `/api/flights/locations/iata/${iata}`,
      customConfig
    )
  }

  async getAllAirlines() {
    return (await this.get<{ airlines: Airline[] }>("/api/flights/airlines"))
      .airlines
  }

  getFlightBooking(id: number): Promise<FlightBookingApi> {
    return this.get<FlightBookingApi>(`/api/flight-bookings/${id}`)
  }

  refreshFlightBooking(id: number): Promise<FlightBookingApi> {
    return this.get<FlightBookingApi>(`/api/flight-bookings/${id}/refresh`)
  }

  getFlightBookingOptions(): Promise<FlightBookingFormOptions> {
    return this.get<FlightBookingFormOptions>(`/api/flight-bookings/options`)
  }

  async getFlightBookingMode(): Promise<FlightBookingMode> {
    const res = await this.get<{ mode: FlightBookingMode }>(
      `/api/flight-bookings/booking-mode`
    )
    return res.mode
  }

  createFlightBooking(request: {
    flight_itinerary_id: number
    chat_id?: number
    flight_quote_ref?: string
  }): Promise<FlightBookingApi> {
    return this.post("/api/flight-bookings", request)
  }

  vendorCheckout(
    flightBookingId: number,
    travelers?: FlightTravelers
  ): Promise<VendorCheckoutResponse> {
    return this.post(`/api/flight-bookings/${flightBookingId}/sabre_checkout`, {
      flight_booking_id: flightBookingId,
      passengers: travelers
        ? {
            adults: travelers.adult_count,
            children: travelers.child_count,
            infants_in_seat: travelers.infant_in_seat_count,
            infants_on_lap: travelers.infant_on_lap_count,
          }
        : undefined,
    })
  }

  vendorCheckoutEvent(
    flightBookingId: number,
    vendor: string,
    eventType: string,
    checkoutId?: string,
    reservationCompleted?: boolean,
    errors?: CheckoutError[]
  ): Promise<FlightBookingApi> {
    return this.post(
      `/api/flight-bookings/${flightBookingId}/vendor_checkout_event`,
      {
        vendor,
        event_type: eventType,
        checkout_id: checkoutId,
        reservation_completed: reservationCompleted,
        errors,
      }
    )
  }

  buildBookingCaptureEndpoint(
    flightBookingId: number
  ): Promise<PCIVaultCaptureEndpoint> {
    return this.post<PCIVaultCaptureEndpoint>(
      `/api/flight-bookings/${flightBookingId}/capture_endpoint`
    )
  }

  async captureBookingVaultData(
    captureEndpoint: PCIVaultCaptureEndpoint,
    data: CreditCardFormValidated
  ): Promise<PCIVaultCaptureResponse> {
    const url = new URL("https://api.pcivault.io")
    url.pathname = captureEndpoint.url_path
    const res = await rawAxios.post<PCIVaultCaptureResponse>(
      url.toString(),
      data,
      {
        headers: {
          "X-PCIVault-Capture-Secret": captureEndpoint.secret,
        },
      }
    )

    return res.data
  }

  submitFlightBooking(
    flightBookingId: number,
    request: {
      travelers: FlightTravelerFormValidated[]
      billing: BillingFormValidated
      seat_map_details: FlightSeatMapDetails[]
      capture_details: PCIVaultCaptureResponse & { unique_id: string }
    }
  ): Promise<FlightBookingApi> {
    return this.post<FlightBookingApi>(
      `/api/flight-bookings/${flightBookingId}/submit`,
      request
    )
  }

  getFlightItinerary(
    id: number,
    customConfig?: BaseApiRequestConfig
  ): Promise<FlightItineraryApi> {
    return this.get<FlightItineraryApi>(
      `/api/flight-itineraries/${id}`,
      customConfig
    )
  }

  createFlightItinerary(
    request: SaveFlightItinerary
  ): Promise<FlightItineraryApi> {
    return this.post("/api/flight-itineraries", request)
  }

  updateFlightItinerary(
    id: number,
    request: SaveFlightItinerary
  ): Promise<FlightItineraryApi> {
    return this.put(`/api/flight-itineraries/${id}`, request)
  }

  getFlightSeatMap(
    vendorSeatMapId: string,
    customConfig?: BaseApiRequestConfig
  ): Promise<FlightSeatMapResponse> {
    return this.get<FlightSeatMapResponse>(
      `/api/flight-itineraries/seat_map/${vendorSeatMapId}`,
      customConfig
    )
  }

  async addOrUpdateEventsForFlight(chatId: number, flightItineraryId: number) {
    return await this.post<{
      trip: Trip
      itinerary: Itinerary
    }>(`/api/flights/${flightItineraryId}/events/${chatId}`)
  }

  async addFlightToTrip(tripRef: string, flightItineraryId: number) {
    return await this.post<{
      trip: Trip
      itinerary: Itinerary
    }>(`/api/flights/${flightItineraryId}/events`, { trip_ref: tripRef })
  }

  async addFlightsToTrip(tripRef: string, flightItineraryIds: number[]) {
    return await this.post<{
      trip: Trip
      itinerary: Itinerary
    }>(`/api/trips/${tripRef}/flights/batch`, {
      flight_itinerary_ids: flightItineraryIds,
    })
  }

  async deleteEventsForFlight(chatId: number, flightItineraryId: number) {
    return await this.delete<{
      deleted_itinerary_events: ItineraryEvent[]
    }>(`/api/flights/${flightItineraryId}/events/${chatId}`)
  }

  async deleteFlightFromTrip(tripRef: string, flightItineraryId: number) {
    return await this.delete<{
      deleted_itinerary_events: ItineraryEvent[]
    }>(`/api/flights/${flightItineraryId}/events`, {
      data: { trip_ref: tripRef },
    })
  }

  cancelFlightBooking(id: number): Promise<FlightBookingApi> {
    return this.post(`/api/flight-bookings/${id}/cancel`)
  }

  cancelFlightSlice(
    flightBookingId: number,
    flightSliceId: number
  ): Promise<FlightItineraryApi> {
    return this.post(
      `/api/flight-bookings/${flightBookingId}/cancel-slice/${flightSliceId}`
    )
  }
}
